/**
 * Supabase Realtime subscription for a single ai_job row.
 *
 * Subscribes to UPDATE events on `ai_jobs` filtered by id.
 * On status change, updates the React Query cache for the consultas list.
 *
 * Usage:
 *   useJobRealtime(jobId)   — subscribes while jobId is non-null
 */
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { queryKeys } from "@/lib/api/queryKeys";
import type { ConsultaAI } from "../types";

interface AiJobUpdate {
  id: string;
  status: string;
  failed_at_stage: string | null;
}

export function useJobRealtime(jobId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!jobId) return;

    const channel = supabase
      .channel(`job-${jobId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "ai_jobs",
          filter: `id=eq.${jobId}`,
        },
        (payload) => {
          const updated = payload.new as AiJobUpdate;
          // Patch the cached consultas list
          queryClient.setQueriesData<ConsultaAI[]>(
            { queryKey: queryKeys.consultas.all },
            (old) => {
              if (!old) return old;
              return old.map((c) =>
                c.aiJobId === jobId
                  ? {
                      ...c,
                      jobStatus: updated.status,
                      jobFailedAtStage: updated.failed_at_stage ?? null,
                    }
                  : c,
              );
            },
          );

          // If terminal state reached, invalidate to get fresh data from DB
          if (updated.status === "done" || updated.status === "failed") {
            void queryClient.invalidateQueries({
              queryKey: queryKeys.consultas.all,
            });
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [jobId, queryClient]);
}
